/**
 * Which cached queries each live event makes stale. Keys are the PREFIXES the
 * @inferrex/api-client hooks put first in their query keys (the route path after /v1/, or the
 * hook's declared key), so invalidating `['pipelines']` refreshes the list, every detail page and
 * its sub-lists at once. A guard test checks every prefix below is one a hook actually uses.
 *
 * Includes the events hand-off README rule 4 lists as declared but not yet listened to:
 * sync.started, sync.failed, sync.record.quarantined, inference.awaiting-review,
 * manifest.connection.updated, manifest.connection.approved, manifest.mapping.approved,
 * manifest.pipeline.activated, manifest.pipeline.paused, match.started, match.completed,
 * match.failed, gamification.scheme.applied.
 *
 * Pages add their own on top with useLiveInvalidation (a narrower key, or an event this map
 * does not cover); this map is what every page gets for free.
 */
import type { QueryKey } from '@tanstack/react-query';

export const README_RULE_4_EVENTS = [
  'sync.started',
  'sync.failed',
  'sync.record.quarantined',
  'inference.awaiting-review',
  'manifest.connection.updated',
  'manifest.connection.approved',
  'manifest.mapping.approved',
  'manifest.pipeline.activated',
  'manifest.pipeline.paused',
  'match.started',
  'match.completed',
  'match.failed',
  'gamification.scheme.applied',
] as const;

const SYNC: QueryKey[] = [['pipelines'], ['jobs'], ['pipeline-events'], ['monitoring']];

export const LIVE_INVALIDATIONS: Readonly<Record<string, readonly QueryKey[]>> = {
  // Sync runs
  'sync.started': SYNC,
  'sync.completed': SYNC,
  'sync.failed': [...SYNC, ['quarantine']],
  'sync.record.quarantined': [['pipelines'], ['quarantine']],
  'sync.anomaly.detected': [['pipelines']],
  // Reading a system's API, and its description changing
  'inference.awaiting-review': [['connections']],
  'inference.failed': [['connections']],
  'manifest.connection.created': [['connections']],
  'manifest.connection.updated': [['connections'], ['mappings']],
  'manifest.connection.approved': [['connections']],
  // Mappings and pipelines
  'manifest.mapping.approved': [['mappings'], ['pipelines']],
  'manifest.pipeline.activated': [['pipelines']],
  'manifest.pipeline.paused': [['pipelines']],
  'match.started': [['mappings'], ['jobs']],
  'match.completed': [['mappings'], ['connections'], ['jobs']],
  'match.failed': [['mappings'], ['jobs']],
  // Repairs
  'healing.failure.received': [['healing']],
  'healing.tier1.resolved': [['healing'], ['pipelines']],
  'healing.tier2.notified': [['healing'], ['pipelines'], ['notifications']],
  'healing.tier3.escalated': [['healing'], ['pipelines'], ['notifications']],
  // Engagement
  'gamification.scheme.applied': [['gamification']],
  // Workspace
  'customer.usage.threshold': [['workspace', 'usage'], ['notifications']],
};
